/**
 * `Continue with Google` and `Continue with Facebook`.
 *
 * The one deliberate exception in this codebase to "no hex in a component". Both companies
 * publish binding specifications for these buttons — the mark, the exact wording, the minimum
 * height, the background, the border and the text colour, in a light and a dark variant — and
 * `docs/DESIGN_SYSTEM.md` states that this is where our tokens yield. A recognisable button is
 * what makes the flow trustworthy, and both companies enforce it at review time.
 *
 * What our system still governs, and what must not be traded away:
 *   • the brass focus ring, via `ring-focus` — neither guideline forbids a focus outline, and
 *     removing it would make the first control on the card keyboard-invisible;
 *   • the 4 px radius, which is what Google's own square variant specifies anyway;
 *   • the disabled treatment and the live region, which are ours because neither vendor says.
 *
 * Followed from the specifications:
 *   Google  — logo 18 px, 14 px Roboto Medium, ≥ 40 px tall, light `#FFFFFF`/`#747775`/`#1F1F1F`,
 *             dark `#131314`/`#8E918F`/`#E3E3E3`, wording "Continue with Google".
 *   Facebook — white "f" on Facebook Blue `#1877F2`, ≥ 28 px tall, wording
 *             "Continue with Facebook". Meta fixes the button's colour, so the surface around
 *             it changes and the button does not.
 */

import type { CSSProperties, ReactElement } from 'react'

import { cn } from '../../../lib/cn'
import { useT } from '../../../lib/i18n'
import { FacebookMark, GoogleMark } from './marks'
import { SOCIAL_LABEL } from './contract'
import type { SocialProvider } from './contract'

/** Paper is the sign-in card and the settings panel; chrome is any dark surface. */
export type ProviderSurface = 'paper' | 'chrome'

export interface ProviderButtonProps {
  provider: SocialProvider
  surface?: ProviderSurface
  onSelect: (provider: SocialProvider) => void
  /** This provider's redirect is being opened. */
  busy?: boolean
  /** Another provider's redirect is being opened, or the card is otherwise blocked. */
  disabled?: boolean
  /** Overrides the wording only where a vendor sanctions an alternative. */
  label?: string
}

const HEIGHT = 44
const RADIUS = 4
const EDGE = 12

/**
 * Roboto first because both specifications name it, then the faces a device is likely to have.
 * Our own Archivo is deliberately absent: the vendors' wording is set in their type, not ours.
 */
const VENDOR_FONT = "Roboto, 'Helvetica Neue', Helvetica, Arial, sans-serif"

const SHELL: CSSProperties = {
  position: 'relative',
  display: 'flex',
  width: '100%',
  height: HEIGHT,
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: RADIUS,
  borderStyle: 'solid',
  borderWidth: 1,
  paddingInline: HEIGHT,
  fontFamily: VENDOR_FONT,
  fontSize: 14,
  lineHeight: '20px',
  letterSpacing: '0.15px',
  cursor: 'pointer',
  transitionProperty: 'filter, opacity',
  transitionDuration: 'var(--dur-1)',
  transitionTimingFunction: 'var(--ease-out)',
}

const MARK_SLOT: CSSProperties = {
  position: 'absolute',
  insetInlineStart: EDGE,
  top: '50%',
  transform: 'translateY(-50%)',
  display: 'flex',
  alignItems: 'center',
}

const GOOGLE_LIGHT: CSSProperties = {
  backgroundColor: '#FFFFFF',
  borderColor: '#747775',
  color: '#1F1F1F',
  fontWeight: 500,
}

const GOOGLE_DARK: CSSProperties = {
  backgroundColor: '#131314',
  borderColor: '#8E918F',
  color: '#E3E3E3',
  fontWeight: 500,
}

const FACEBOOK_BLUE = '#1877F2'

const FACEBOOK: CSSProperties = {
  backgroundColor: FACEBOOK_BLUE,
  borderColor: FACEBOOK_BLUE,
  color: '#FFFFFF',
  fontWeight: 600,
}

/**
 * One button, in the vendor's clothes.
 *
 * `hover:brightness-*` rather than a second hex: it reproduces each vendor's own hover
 * (a light grey wash on the white button, a deeper blue on Facebook's) without inventing a
 * colour value neither of them published.
 */
export const ProviderButton = ({
  provider,
  surface = 'paper',
  onSelect,
  busy = false,
  disabled = false,
  label,
}: ProviderButtonProps): ReactElement => {
  const t = useT()
  const blocked = busy || disabled
  const isGoogle = provider === 'google'

  const skin: CSSProperties = isGoogle
    ? surface === 'chrome'
      ? GOOGLE_DARK
      : GOOGLE_LIGHT
    : FACEBOOK

  // Both vendors publish the wording *and its translations* — "Continua con Google" is theirs,
  // not ours. The company name inside it is the one part that never changes.
  const text = label ?? t('auth.social.continueWith', { provider: SOCIAL_LABEL[provider] })

  return (
    <button
      type="button"
      onClick={() => {
        onSelect(provider)
      }}
      disabled={blocked}
      aria-busy={busy || undefined}
      className={cn(
        'ring-focus select-none',
        blocked ? 'cursor-not-allowed' : 'hover:brightness-[0.96] active:brightness-[0.92]',
      )}
      style={{ ...SHELL, ...skin, opacity: blocked ? 0.6 : 1 }}
    >
      <span style={MARK_SLOT}>
        {isGoogle ? (
          <GoogleMark size={18} />
        ) : (
          <FacebookMark size={20} colour="#FFFFFF" />
        )}
      </span>
      <span className="truncate">{text}</span>
    </button>
  )
}
